import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2 as Grid,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  CheckCircle,
  Error,
  NotInterested,
  Comment,
  Add,
  Send,
  Print,
  Visibility,
  WarningAmber,
  ArrowBack,
  ArrowBackIos,
  ArrowForwardIos,
  PhotoCamera,
  CloudUpload,
  Image,
  LocationOn,
  Info,
  Close,
  Download,
  ExpandMore,
  ChevronRight,
  Folder,
  FolderOpen,
  Edit,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PpMobilePhotoCaptureComponent from "../Photos/PpMobilePhotoCaptureComponent";
import PpPhotoAnnotationTools from "../Photos/PpPhotoAnnotationTools";
import {
  loadWorkbench,
  savePageCheck,
  approveSubmission,
  returnForCorrections,
  markAllPagesCompliant,
} from "../../../../store/actions/ppActions";
import FileDropzone from "../components/FileDropzone";

const PpReviewWorkbenchPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const workbenchState = useSelector(
    (state) => state[currentSession]?.pp?.workbench
  );
  const submission = workbenchState?.data;
  const pages = useMemo(() => workbenchState?.pages || [], [workbenchState]);
  const loading = workbenchState?.loading || false;

  const [selectedPage, setSelectedPage] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(""); // 'approve' or 'return'
  const [actionNotes, setActionNotes] = useState("");
  const [codeSection, setCodeSection] = useState("");
  const [addingComment, setAddingComment] = useState(false);

  // Photo upload states
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoType, setPhotoType] = useState(""); // 'property', 'condition', 'reference'
  const [propertyPhotos, setPropertyPhotos] = useState([
    {
      id: "workbench_prop_1",
      filename: "property_overview.jpg",
      name: "property_overview.jpg",
      url: "https://via.placeholder.com/800x600/0066cc/ffffff?text=Property+Overview",
      dataUrl:
        "https://via.placeholder.com/800x600/0066cc/ffffff?text=Property+Overview",
      type: "property",
    },
    {
      id: "workbench_prop_2",
      filename: "property_details.jpg",
      name: "property_details.jpg",
      url: "https://via.placeholder.com/800x600/0066cc/ffffff?text=Property+Details",
      dataUrl:
        "https://via.placeholder.com/800x600/0066cc/ffffff?text=Property+Details",
      type: "property",
    },
  ]);
  const [conditionPhotos, setConditionPhotos] = useState([
    {
      id: "workbench_cond_1",
      filename: "condition_assessment.jpg",
      name: "condition_assessment.jpg",
      url: "https://via.placeholder.com/800x600/ff6600/ffffff?text=Condition+Assessment",
      dataUrl:
        "https://via.placeholder.com/800x600/ff6600/ffffff?text=Condition+Assessment",
      type: "condition",
    },
  ]);
  const [referencePhotos, setReferencePhotos] = useState([
    {
      id: "workbench_ref_1",
      filename: "reference_document.jpg",
      name: "reference_document.jpg",
      url: "https://via.placeholder.com/800x600/cc0000/ffffff?text=Reference+Document",
      dataUrl:
        "https://via.placeholder.com/800x600/cc0000/ffffff?text=Reference+Document",
      type: "reference",
    },
  ]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoNotes, setPhotoNotes] = useState("");

  // Photo capture and annotation states
  const [photoCaptureOpen, setPhotoCaptureOpen] = useState(false);
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // File viewer states
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  // Tree layout states
  const [expandedDocuments, setExpandedDocuments] = useState(new Set());

  // Mock data for issue types and code sections
  const issueTypes = [
    "Structural",
    "Architectural",
    "Electrical",
    "Mechanical",
    "Plumbing",
    "Energy",
    "Fire Safety",
    "Accessibility",
    "Zoning",
  ];

  const buildingCodes = [
    "IBC 1805.4.2 - Foundation Requirements",
    "IBC 1604.3 - Live Load Requirements",
    "IRC R502.3.1 - Floor Joist Sizing",
    "IRC R602.3 - Wall Framing Requirements",
    "NFPA 70 - Electrical Code Requirements",
    "IMC 401.2 - Mechanical Ventilation",
    "IPC 305.1 - Plumbing Fixture Requirements",
  ];

  useEffect(() => {
    if (id) {
      dispatch(loadWorkbench(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (pages.length > 0 && !selectedPage) {
      setSelectedPage(pages[0]);
    }
  }, [pages, selectedPage]);

  const handlePageStatusChange = async (pageId, newStatus) => {
    try {
      const page = pages.find((p) => p.id === pageId);

      // Toggle logic: if clicking the same status, deselect (set to not_reviewed)
      const currentStatus = page.status;
      const finalStatus =
        currentStatus === newStatus ? "not_reviewed" : newStatus;

      // Clear issues if status is compliant, otherwise keep existing issues
      const updatedIssues =
        finalStatus === "compliant" ? [] : page.issues || [];

      await dispatch(
        savePageCheck(
          id,
          pageId,
          finalStatus,
          page.comments || [],
          updatedIssues
        )
      );

      // Update the selectedPage state to reflect the new status
      if (selectedPage && selectedPage.id === pageId) {
        setSelectedPage({
          ...selectedPage,
          status: finalStatus,
          issues: updatedIssues,
        });
      }
    } catch (error) {
      console.error("Failed to save page status:", error);
    }
  };

  const handlePreviousPage = useCallback(() => {
    if (!selectedPage || pages.length === 0) return;
    const currentIndex = pages.findIndex((p) => p.id === selectedPage.id);
    if (currentIndex > 0) {
      setSelectedPage(pages[currentIndex - 1]);
    }
  }, [selectedPage, pages]);

  const handleNextPage = useCallback(() => {
    if (!selectedPage || pages.length === 0) return;
    const currentIndex = pages.findIndex((p) => p.id === selectedPage.id);
    if (currentIndex < pages.length - 1) {
      setSelectedPage(pages[currentIndex + 1]);
    }
  }, [selectedPage, pages]);

  const handleIssueToggle = (pageId, issue) => {
    if (!selectedPage || selectedPage.id !== pageId) return;

    const currentIssues = selectedPage.issues || [];
    const newIssues = currentIssues.includes(issue)
      ? currentIssues.filter((i) => i !== issue)
      : [...currentIssues, issue];

    // Update local state immediately
    setSelectedPage({
      ...selectedPage,
      issues: newIssues,
    });

    // Update in Redux
    dispatch(
      savePageCheck(
        id,
        pageId,
        selectedPage.status,
        selectedPage.comments || [],
        newIssues
      )
    );
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      ) {
        return; // Don't interfere with input fields
      }

      if (event.key === "ArrowLeft") {
        handlePreviousPage();
      } else if (event.key === "ArrowRight") {
        handleNextPage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handlePreviousPage, handleNextPage]);

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedPage) return;

    setAddingComment(true);

    const comment = {
      id: Date.now().toString(),
      text: newComment,
      author: "Current Reviewer", // Should come from user state
      timestamp: new Date().toISOString(),
      codeSection: codeSection || undefined,
    };

    try {
      const existingComments = selectedPage.comments || [];
      const updatedComments = [...existingComments, comment];

      // Update local state immediately for better UX
      setSelectedPage({
        ...selectedPage,
        comments: updatedComments,
      });

      await dispatch(
        savePageCheck(
          id,
          selectedPage.id,
          selectedPage.status,
          updatedComments,
          selectedPage.issues || []
        )
      );

      setNewComment("");
      setCodeSection("");
      setCommentDialogOpen(false);
    } catch (error) {
      console.error("Failed to add comment:", error);
      // Revert the local state change if the API call failed
      setSelectedPage({
        ...selectedPage,
        comments: selectedPage.comments || [],
      });
    } finally {
      setAddingComment(false);
    }
  };

  const handleMarkAllCompliant = async () => {
    try {
      await dispatch(markAllPagesCompliant(id));
    } catch (error) {
      console.error("Failed to mark all pages as compliant:", error);
    }
  };

  const handleFinalizeAction = async () => {
    try {
      if (actionType === "approve") {
        await dispatch(approveSubmission(id, actionNotes));
        navigate("/pp/submissions");
      } else if (actionType === "return") {
        await dispatch(returnForCorrections(id, actionNotes));
        navigate("/pp/submissions");
      }
      setActionDialogOpen(false);
    } catch (error) {
      console.error("Failed to finalize action:", error);
    }
  };

  // Photo upload handlers
  const handleOpenPhotoDialog = (type) => {
    setPhotoType(type);
    setPhotoNotes("");
    setPhotoDialogOpen(true);
  };

  const getCurrentPhotos = () => {
    switch (photoType) {
      case "property":
        return propertyPhotos;
      case "condition":
        return conditionPhotos;
      case "reference":
        return referencePhotos;
      default:
        return [];
    }
  };

  const setCurrentPhotos = (photos) => {
    switch (photoType) {
      case "property":
        setPropertyPhotos(photos);
        break;
      case "condition":
        setConditionPhotos(photos);
        break;
      case "reference":
        setReferencePhotos(photos);
        break;
      default:
        break;
    }
  };

  const handlePhotoSelection = (newPhotos) => {
    const currentPhotos = getCurrentPhotos();
    setCurrentPhotos([...currentPhotos, ...newPhotos]);
  };

  const handlePhotoRemove = (photoId) => {
    const currentPhotos = getCurrentPhotos();
    setCurrentPhotos(currentPhotos.filter((photo) => photo.id !== photoId));
  };

  const handleUploadPhotos = async () => {
    if (getCurrentPhotos().length === 0) return;

    try {
      setUploadingPhotos(true);

      // Mock API call for photo upload
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Successful upload notification

      setPhotoDialogOpen(false);
      setPhotoNotes("");
    } catch (error) {
      console.error("Failed to upload photos:", error);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const getPhotoTypeTitle = (type) => {
    switch (type) {
      case "property":
        return "Property Context Photos";
      case "condition":
        return "Site Condition Photos";
      case "reference":
        return "Reference Photos";
      default:
        return "Photos";
    }
  };

  const getPhotoTypeDescription = (type) => {
    switch (type) {
      case "property":
        return "Upload photos showing the overall property context, exterior views, and site overview to understand the project location.";
      case "condition":
        return "Document current site conditions, existing structures, and any relevant environmental factors.";
      case "reference":
        return "Add reference photos that support your review decisions, code compliance verification, or comparative examples.";
      default:
        return "Upload photos related to this submission.";
    }
  };

  // File viewer handlers
  const handleViewFile = (file, allFiles) => {
    setSelectedFiles(allFiles);
    setCurrentFileIndex(allFiles.findIndex((f) => f.id === file.id));
    setSelectedFile(file);
    setFileViewerOpen(true);
  };

  const handleNextFile = () => {
    if (currentFileIndex < selectedFiles.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
      setSelectedFile(selectedFiles[currentFileIndex + 1]);
    }
  };

  const handlePrevFile = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
      setSelectedFile(selectedFiles[currentFileIndex - 1]);
    }
  };

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setSelectedFile(null);
    setSelectedFiles([]);
    setCurrentFileIndex(0);
  };

  const handleDownloadSingleFile = (file) => {
    const link = document.createElement("a");
    link.href = `/api/files/${file.id}/download`;
    link.download = file.name;
    link.click();
  };

  // Function to format file names for better display
  const formatFileName = (fileName) => {
    if (!fileName) return "Unknown Document";

    // Remove file extension
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");

    // Convert to title case and replace underscores/hyphens with spaces
    const formatted = nameWithoutExt
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

    // Handle common document types
    if (formatted.toLowerCase().includes("architectural")) {
      return "Architectural Plans";
    } else if (formatted.toLowerCase().includes("structural")) {
      return "Structural Plans";
    } else if (formatted.toLowerCase().includes("site")) {
      return "Site Plan";
    } else if (formatted.toLowerCase().includes("energy")) {
      return "Energy Forms";
    } else if (formatted.toLowerCase().includes("electrical")) {
      return "Electrical Plans";
    } else if (formatted.toLowerCase().includes("plumbing")) {
      return "Plumbing Plans";
    } else if (formatted.toLowerCase().includes("mechanical")) {
      return "Mechanical Plans";
    }

    return formatted;
  };

  // Tree layout handlers
  const handleToggleDocument = (fileId) => {
    const newExpanded = new Set(expandedDocuments);
    if (newExpanded.has(fileId)) {
      newExpanded.delete(fileId);
    } else {
      newExpanded.add(fileId);
      // Auto-select the first page of the expanded document
      const file = submission.files?.find((f) => f.id === fileId);
      if (file) {
        const filePages = pages.filter(
          (page) => page.documentName === file.name.split(".")[0]
        );
        if (filePages.length > 0 && !selectedPage) {
          setSelectedPage(filePages[0]);
        }
      }
    }
    setExpandedDocuments(newExpanded);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "compliant":
        return <CheckCircle color="success" />;
      case "needs_correction":
        return <Error color="error" />;
      case "not_applicable":
        return <NotInterested color="disabled" />;
      default:
        return <WarningAmber color="warning" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "compliant":
        return "success";
      case "needs_correction":
        return "error";
      case "not_applicable":
        return "default";
      default:
        return "warning";
    }
  };

  const getProgressStats = () => {
    // Get pages for expanded documents
    const expandedFiles = Array.from(expandedDocuments)
      .map((fileId) => submission.files?.find((f) => f.id === fileId))
      .filter(Boolean);

    const pagesToUse =
      expandedFiles.length > 0
        ? pages.filter((page) =>
            expandedFiles.some(
              (file) => page.documentName === file.name.split(".")[0]
            )
          )
        : pages;

    const total = pagesToUse.length;
    const reviewed = pagesToUse.filter(
      (p) => p.status !== "not_reviewed"
    ).length;
    const compliant = pagesToUse.filter((p) => p.status === "compliant").length;
    const needsCorrection = pagesToUse.filter(
      (p) => p.status === "needs_correction"
    ).length;
    const notApplicable = pagesToUse.filter(
      (p) => p.status === "not_applicable"
    ).length;

    return {
      total,
      reviewed,
      compliant,
      needsCorrection,
      notApplicable,
      progress: total > 0 ? (reviewed / total) * 100 : 0,
      currentFile:
        expandedFiles.length === 1
          ? formatFileName(expandedFiles[0].name)
          : expandedFiles.length > 1
            ? `${expandedFiles.length} Documents`
            : "All Documents",
    };
  };

  const stats = getProgressStats();

  // For approval logic, we need to check ALL pages across ALL files
  const allPagesStats = (() => {
    const total = pages.length;
    const reviewed = pages.filter((p) => p.status !== "not_reviewed").length;
    const needsCorrection = pages.filter(
      (p) => p.status === "needs_correction"
    ).length;
    return { total, reviewed, needsCorrection };
  })();

  const canApprove =
    allPagesStats.reviewed === allPagesStats.total &&
    allPagesStats.needsCorrection === 0;
  const allPagesReviewed = allPagesStats.reviewed === allPagesStats.total;

  if (loading) {
    return (
      <Box sx={{ width: "100%", mt: 0 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!submission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Submission not found or failed to load workbench data.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/submissions")}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography color="primary" variant="h4">
            Review Workbench - {submission.id}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {submission.address} • {submission.contractor.name}
          </Typography>
        </Box>
      </Box>

      {/* Progress Stats */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ sm: 12, md: 8 }}>
              <Typography variant="h6" gutterBottom>
                {stats.currentFile
                  ? `${stats.currentFile} Review Progress`
                  : "Overall Review Progress"}{" "}
                ({stats.reviewed}/{stats.total} pages)
                {stats.total === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                    sx={{ ml: 1 }}
                  >
                    (No pages found for this document)
                  </Typography>
                )}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={stats.progress}
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <Chip
                  icon={<CheckCircle />}
                  label={`${stats.compliant} Compliant`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<Error />}
                  label={`${stats.needsCorrection} Need Correction`}
                  color="error"
                  variant="outlined"
                />
                <Chip
                  icon={<NotInterested />}
                  label={`${stats.notApplicable} N/A`}
                  color="default"
                  variant="outlined"
                />
              </Box>
            </Grid>
            <Grid size={{ sm: 12, md: 4 }} sx={{ textAlign: "right" }}>
              {/* Photo Upload Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Property Documentation
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Image />}
                    onClick={() => handleOpenPhotoDialog("property")}
                    sx={{ minWidth: "120px" }}
                  >
                    Property ({propertyPhotos.length})
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LocationOn />}
                    onClick={() => handleOpenPhotoDialog("condition")}
                    sx={{ minWidth: "120px" }}
                  >
                    Condition ({conditionPhotos.length})
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Info />}
                    onClick={() => handleOpenPhotoDialog("reference")}
                    sx={{ minWidth: "120px" }}
                  >
                    Reference ({referencePhotos.length})
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PhotoCamera />}
                    color="success"
                    onClick={() => setPhotoCaptureOpen(true)}
                    sx={{ minWidth: "140px" }}
                  >
                    Capture Photos
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    color="secondary"
                    onClick={() => navigate("/pp/photos")}
                    sx={{ minWidth: "120px" }}
                  >
                    Photo Gallery
                  </Button>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleMarkAllCompliant}
                  disabled={stats.compliant === stats.total}
                  size="small"
                >
                  Mark All Compliant ({stats.currentFile || "All Files"})
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    setActionType("approve");
                    setActionDialogOpen(true);
                  }}
                  disabled={!canApprove}
                  startIcon={<CheckCircle />}
                >
                  Approve Submission
                </Button>

                {/* Stamping workflow button */}
                {canApprove && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate(`/pp/submissions/${id}/stamp`)}
                    startIcon={<Print />}
                  >
                    Stamping Workflow
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setActionType("return");
                    setActionDialogOpen(true);
                  }}
                  disabled={!allPagesReviewed}
                  startIcon={<Send />}
                >
                  Return for Corrections
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: "flex", gap: 2 }}>
        {/* File Tabs and Pages List */}
        <Card
          sx={{
            width: 350,
            minWidth: 350,
            display: "flex",
            flexDirection: "column",
            p: 1,
          }}
        >
          {/* Document Tree Structure */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ px: 2, py: 1, fontWeight: 600, color: "text.secondary" }}
            >
              📁 Documents ({submission.files?.length || 0})
            </Typography>
            {submission.files?.map((file, index) => {
              // Get stats for this specific file
              const filePages = pages.filter(
                (page) => page.documentName === file.name.split(".")[0]
              );
              const fileStats = {
                total: filePages.length,
                reviewed: filePages.filter((p) => p.status !== "not_reviewed")
                  .length,
                compliant: filePages.filter((p) => p.status === "compliant")
                  .length,
                needsCorrection: filePages.filter(
                  (p) => p.status === "needs_correction"
                ).length,
                notApplicable: filePages.filter(
                  (p) => p.status === "not_applicable"
                ).length,
              };

              const isExpanded = expandedDocuments.has(file.id);
              const hasIssues = fileStats.needsCorrection > 0;

              return (
                <Box key={file.id}>
                  {/* Document Folder Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      borderRadius: 1,
                      mx: 1,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      backgroundColor: isExpanded
                        ? "action.selected"
                        : "transparent",
                    }}
                    onClick={() => handleToggleDocument(file.id)}
                  >
                    <IconButton size="small" sx={{ mr: 0.5, p: 0.25 }}>
                      {isExpanded ? <ExpandMore /> : <ChevronRight />}
                    </IconButton>
                    <IconButton size="small" sx={{ mr: 1, p: 0.25 }}>
                      {isExpanded ? (
                        <FolderOpen color="primary" />
                      ) : (
                        <Folder color="action" />
                      )}
                    </IconButton>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          lineHeight: 1.2,
                          color: hasIssues ? "error.main" : "text.primary",
                        }}
                      >
                        {formatFileName(file.name)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1 }}
                      >
                        {fileStats.reviewed}/{fileStats.total} reviewed
                        {hasIssues && (
                          <span style={{ color: "red", marginLeft: "8px" }}>
                            • {fileStats.needsCorrection} issues
                          </span>
                        )}
                      </Typography>
                    </Box>
                    {/* Action buttons */}
                    <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
                      <Tooltip title="View Document">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewFile(file, submission.files);
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download Document">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadSingleFile(file);
                          }}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Expanded Document Details */}
                  {isExpanded && (
                    <Box sx={{ pl: 4, pr: 2, pb: 1 }}>
                      {/* Document Info */}
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          {formatFileName(file.name)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          {file.size || "Unknown size"} •{" "}
                          {file.documentType || "PDF"} • ID: {file.id}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Uploaded:{" "}
                          {file.uploadDate || new Date().toLocaleDateString()}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          Pages: {fileStats.total} • Reviewed:{" "}
                          {fileStats.reviewed} • Issues:{" "}
                          {fileStats.needsCorrection}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ flexGrow: 1, overflow: "auto", overflowX: "hidden" }}>
            {/* Pages List - Show pages from expanded documents */}
            {expandedDocuments.size > 0 ? (
              <List dense>
                {pages
                  .filter((page) => {
                    // Show pages from any expanded document
                    return Array.from(expandedDocuments).some((fileId) => {
                      const file = submission.files?.find(
                        (f) => f.id === fileId
                      );
                      return (
                        file && page.documentName === file.name.split(".")[0]
                      );
                    });
                  })
                  .map((page) => (
                    <ListItem
                      key={page.id}
                      button
                      selected={selectedPage?.id === page.id}
                      onClick={() => setSelectedPage(page)}
                    >
                      <ListItemIcon>{getStatusIcon(page.status)}</ListItemIcon>
                      <ListItemText
                        primary={`${page.documentName} - ${page.pageName}`}
                        primaryTypographyProps={{
                          sx: {
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                          },
                        }}
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 0.5,
                              flexWrap: "wrap",
                              overflow: "hidden",
                            }}
                          >
                            <Chip
                              label={
                                page.status?.replace("_", " ") || "Not Reviewed"
                              }
                              color={getStatusColor(page.status)}
                              size="small"
                            />
                            {page.comments?.length > 0 && (
                              <Chip
                                icon={<Comment />}
                                label={page.comments.length}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Folder sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Select a document to view pages
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Click on a document folder above to expand and see its pages
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        {/* Page Review Panel */}
        <Card
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {selectedPage ? (
            <>
              <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {/* Page Navigation */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Tooltip title="Previous Page">
                        <span>
                          <IconButton
                            onClick={handlePreviousPage}
                            disabled={
                              !selectedPage ||
                              pages.findIndex(
                                (p) => p.id === selectedPage.id
                              ) === 0
                            }
                          >
                            <ArrowBackIos />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Typography
                        variant="body2"
                        sx={{ minWidth: 50, textAlign: "center" }}
                      >
                        {selectedPage
                          ? pages.findIndex((p) => p.id === selectedPage.id) + 1
                          : 0}{" "}
                        / {pages.length}
                      </Typography>
                      <Tooltip title="Next Page">
                        <span>
                          <IconButton
                            onClick={handleNextPage}
                            disabled={
                              !selectedPage ||
                              pages.findIndex(
                                (p) => p.id === selectedPage.id
                              ) ===
                                pages.length - 1
                            }
                          >
                            <ArrowForwardIos />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Typography variant="h6">
                      {selectedPage.documentName} - {selectedPage.pageName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="View PDF Document">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          if (selectedPage && submission.files) {
                            // Find the file that corresponds to this page
                            // Try multiple matching strategies for better compatibility
                            let pageFile = submission.files.find(
                              (file) =>
                                file.name.split(".")[0] ===
                                selectedPage.documentName
                            );

                            // If exact match fails, try partial matching
                            if (!pageFile) {
                              pageFile = submission.files.find(
                                (file) =>
                                  file.name
                                    .toLowerCase()
                                    .includes(
                                      selectedPage.documentName.toLowerCase()
                                    ) ||
                                  selectedPage.documentName
                                    .toLowerCase()
                                    .includes(
                                      file.name.split(".")[0].toLowerCase()
                                    )
                              );
                            }

                            // If still no match, try using the first expanded file
                            if (!pageFile && expandedDocuments.size > 0) {
                              const firstExpandedId =
                                Array.from(expandedDocuments)[0];
                              pageFile = submission.files?.find(
                                (f) => f.id === firstExpandedId
                              );
                            }

                            if (pageFile) {
                              handleViewFile(pageFile, submission.files);
                            } else {
                              // Fallback: open the first file
                              if (submission.files.length > 0) {
                                handleViewFile(
                                  submission.files[0],
                                  submission.files
                                );
                              }
                            }
                          }
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print Page">
                      <IconButton>
                        <Print />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Status Selection */}
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Page Status
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant={
                        selectedPage.status === "compliant"
                          ? "contained"
                          : "outlined"
                      }
                      color="success"
                      size="small"
                      startIcon={<CheckCircle />}
                      onClick={() =>
                        handlePageStatusChange(selectedPage.id, "compliant")
                      }
                    >
                      Compliant
                    </Button>
                    <Button
                      variant={
                        selectedPage.status === "needs_correction"
                          ? "contained"
                          : "outlined"
                      }
                      color="error"
                      size="small"
                      startIcon={<Error />}
                      onClick={() =>
                        handlePageStatusChange(
                          selectedPage.id,
                          "needs_correction"
                        )
                      }
                    >
                      Needs Correction
                    </Button>
                    <Button
                      variant={
                        selectedPage.status === "not_applicable"
                          ? "contained"
                          : "outlined"
                      }
                      color="inherit"
                      size="small"
                      startIcon={<NotInterested />}
                      onClick={() =>
                        handlePageStatusChange(
                          selectedPage.id,
                          "not_applicable"
                        )
                      }
                    >
                      N/A
                    </Button>
                  </Box>
                </Box>

                {/* Issue Categories */}
                {selectedPage.status === "needs_correction" && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Issue Categories
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {issueTypes.map((issue) => (
                        <FormControlLabel
                          key={issue}
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                selectedPage.issues?.includes(issue) || false
                              }
                              onChange={() =>
                                handleIssueToggle(selectedPage.id, issue)
                              }
                            />
                          }
                          label={issue}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>

              {/* PDF Viewer Placeholder */}
              <Box
                sx={{
                  flexGrow: 1,
                  bgcolor: "grey.50",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 400,
                  p: 3,
                  border: 1,
                  borderColor: "grey.300",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  📄 {selectedPage.documentName} - {selectedPage.pageName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Page {selectedPage.id} of {pages.length}
                </Typography>
                <Box sx={{ textAlign: "center", maxWidth: 400 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    <strong>Current Status:</strong>{" "}
                    {selectedPage.status?.replace("_", " ") || "Not Reviewed"}
                  </Typography>
                  {selectedPage.issues?.length > 0 && (
                    <Typography
                      variant="body2"
                      color="error.main"
                      sx={{ mb: 1 }}
                    >
                      <strong>Issues:</strong> {selectedPage.issues.join(", ")}
                    </Typography>
                  )}
                  {selectedPage.comments?.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Comments:</strong> {selectedPage.comments.length}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: "white",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "grey.200",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center" }}
                  >
                    📋 PDF content would be displayed here
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", textAlign: "center", mt: 1 }}
                  >
                    Click "View PDF Page" to open in new tab
                  </Typography>
                </Box>
              </Box>

              {/* Comments Section */}
              <Box
                sx={{
                  p: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  maxHeight: 200,
                  overflow: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2">
                    Comments ({selectedPage.comments?.length || 0})
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setCommentDialogOpen(true)}
                  >
                    Add Comment
                  </Button>
                </Box>

                {selectedPage.comments?.length > 0 ? (
                  selectedPage.comments.map((comment) => (
                    <Paper
                      key={comment.id}
                      sx={{ p: 2, mb: 1, bgcolor: "grey.50" }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {comment.text}
                      </Typography>
                      {comment.codeSection && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ fontWeight: "bold" }}
                        >
                          Code Reference: {comment.codeSection}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1 }}
                      >
                        By {comment.author} •{" "}
                        {new Date(comment.timestamp).toLocaleString()}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    No comments yet
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select a page to review
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      {/* Add Comment Dialog */}
      <Dialog
        open={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comment"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Building Code Reference (Optional)</InputLabel>
            <Select
              value={codeSection}
              label="Building Code Reference (Optional)"
              onChange={(e) => setCodeSection(e.target.value)}
            >
              <MenuItem value="">No code reference</MenuItem>
              {buildingCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCommentDialogOpen(false)}
            disabled={addingComment}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={!newComment.trim() || addingComment}
          >
            {addingComment ? "Adding..." : "Add Comment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === "approve"
            ? "Approve Submission"
            : "Return for Corrections"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {actionType === "approve"
              ? "This will approve the submission and unlock stamping capabilities."
              : "This will return the submission to the contractor for corrections."}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={
              actionType === "approve"
                ? "Approval Notes (Optional)"
                : "Correction Notes (Required)"
            }
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            required={actionType === "return"}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={actionType === "approve" ? "success" : "error"}
            onClick={handleFinalizeAction}
            disabled={actionType === "return" && !actionNotes.trim()}
          >
            {actionType === "approve" ? "Approve" : "Return for Corrections"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PhotoCamera color="primary" />
            {getPhotoTypeTitle(photoType)}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            {getPhotoTypeDescription(photoType)}
          </Alert>

          <FileDropzone
            onFilesSelected={handlePhotoSelection}
            onFileRemove={handlePhotoRemove}
            existingFiles={getCurrentPhotos()}
            acceptedFileTypes={[".jpg", ".jpeg", ".png", ".pdf"]}
            maxFiles={10}
            maxFileSize={5 * 1024 * 1024} // 5MB
            generateFileName={() =>
              `${photoType.toUpperCase()}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            }
            disabled={uploadingPhotos}
          />

          {getCurrentPhotos().length > 0 && (
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Photo Notes (Optional)"
                value={photoNotes}
                onChange={(e) => setPhotoNotes(e.target.value)}
                placeholder={`Add notes about these ${photoType} photos...`}
                disabled={uploadingPhotos}
              />
            </Box>
          )}

          {/* Quick Photo Action Tips */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              📸 Photo Tips:
            </Typography>
            {photoType === "property" && (
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Include front and back views of the property
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Show property boundaries and setbacks
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Document neighboring properties for context
                </Typography>
              </Box>
            )}
            {photoType === "condition" && (
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Document existing utilities and infrastructure
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Show current grading and drainage conditions
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Capture any existing structures or improvements
                </Typography>
              </Box>
            )}
            {photoType === "reference" && (
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Include photos supporting your review decisions
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Document code compliance verification
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Add comparative examples or reference materials
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPhotoDialogOpen(false)}
            disabled={uploadingPhotos}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadPhotos}
            disabled={getCurrentPhotos().length === 0 || uploadingPhotos}
            startIcon={uploadingPhotos ? null : <CloudUpload />}
          >
            {uploadingPhotos
              ? "Uploading..."
              : `Upload ${getCurrentPhotos().length} Photo${getCurrentPhotos().length !== 1 ? "s" : ""}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        maxWidth="lg"
        fullWidth
        fullScreen={window.innerWidth < 768}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">
              {selectedFile?.name || "File Viewer"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {selectedFiles.length > 1 && (
                <Typography variant="body2" color="text.secondary">
                  {currentFileIndex + 1} of {selectedFiles.length}
                </Typography>
              )}
              <IconButton onClick={handleCloseFileViewer} size="small">
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, minHeight: 500 }}>
          {selectedFile && (
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              {/* File Navigation */}
              {selectedFiles.length > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Button
                    startIcon={<ArrowBackIos />}
                    onClick={handlePrevFile}
                    disabled={currentFileIndex === 0}
                    variant="outlined"
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFile.name}
                  </Typography>
                  <Button
                    endIcon={<ArrowForwardIos />}
                    onClick={handleNextFile}
                    disabled={currentFileIndex === selectedFiles.length - 1}
                    variant="outlined"
                  >
                    Next
                  </Button>
                </Box>
              )}

              {/* File Content */}
              <Box sx={{ flexGrow: 1, p: 2 }}>
                {selectedFile.name.toLowerCase().endsWith(".pdf") ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "60vh",
                      border: 1,
                      borderColor: "grey.300",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        📄 {selectedFile.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        PDF viewer would be displayed here
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={() => handleDownloadSingleFile(selectedFile)}
                      >
                        Download PDF
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "60vh",
                      border: 1,
                      borderColor: "grey.300",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        📁 {selectedFile.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        File preview would be displayed here
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={() => handleDownloadSingleFile(selectedFile)}
                      >
                        Download File
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileViewer}>Close</Button>
          {selectedFile && (
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => handleDownloadSingleFile(selectedFile)}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Photo Capture Dialog */}
      <PpMobilePhotoCaptureComponent
        open={photoCaptureOpen}
        onClose={() => setPhotoCaptureOpen(false)}
        onPhotosUploaded={(photos) => {
          // Add photos to the appropriate category
          const newPhotos = photos.map((photo) => ({
            id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: `Photo_${Date.now()}.jpg`,
            type: "property", // Default to property photos
            size: photo.metadata?.fileSize || "Unknown",
            url: photo.dataUrl,
            uploadedAt: new Date().toISOString(),
            notes: photo.notes || "",
          }));
          setPropertyPhotos((prev) => [...prev, ...newPhotos]);
          setPhotoCaptureOpen(false);
        }}
        category="workbench"
        propertyId={submission?.id}
      />

      {/* Photo Annotation Dialog */}
      {selectedPhoto && (
        <Dialog
          open={annotationOpen}
          onClose={() => setAnnotationOpen(false)}
          maxWidth="xl"
          fullWidth
          fullScreen
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Edit />
              Photo Annotation - Workbench
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <PpPhotoAnnotationTools
              photo={selectedPhoto}
              photoList={[
                ...propertyPhotos,
                ...conditionPhotos,
                ...referencePhotos,
              ]}
              onSave={(annotatedPhoto) => {
                setAnnotationOpen(false);
              }}
              onCancel={() => setAnnotationOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default PpReviewWorkbenchPage;
